import type { CampaignOperationalStatus, CampaignStatus } from '@/api/types';
import { Icons } from '@/components/primitives';
import { campaignPauseAction } from '@/lib/campaign-status';
import { fmtDayShort } from '@/lib/datetime';

/** The label and submitted intent come from the same refreshed snapshot. */
export function CampaignDeliveryAction({ status, requestedState, pending, onRequest }: {
  status?: CampaignOperationalStatus | null;
  requestedState: CampaignStatus;
  pending: boolean;
  onRequest: (next: 'live' | 'paused') => void;
}) {
  const current = status?.requested_state ?? requestedState;
  if (current !== 'live' && current !== 'paused') return null;
  // Every flight is over (2026-10-19): there is nothing left to pause or
  // resume. Extend, Duplicate and Archive are what a finished campaign offers,
  // and the API refuses Pause and Resume too.
  if (status?.phase === 'finished') return null;
  const next = campaignPauseAction(status, current);
  const keepPaused = status?.state === 'paused_by_system';
  const label = keepPaused ? 'Keep paused' : next === 'paused' ? 'Pause campaign' : 'Resume campaign';
  // Resuming outside a flight turns nothing on today; say when it will.
  const nextFlight = status?.next_flight_date ? fmtDayShort(status.next_flight_date) : null;
  const resumeTitle = nextFlight && status?.phase === 'between_flights'
    ? `Request live delivery: turns on the next flight ${nextFlight}`
    : nextFlight && status?.phase === 'before_first_flight'
      ? `Request live delivery: turns on when the first flight starts ${nextFlight}`
      : 'Request live delivery when the campaign is ready';
  return <button
    className={`btn ${next === 'live' ? 'btn-primary' : ''}`}
    data-phone="inline"
    disabled={pending}
    aria-label={label}
    title={keepPaused ? 'Keep this campaign paused after recovery' : next === 'paused' ? 'Request paused delivery' : resumeTitle}
    onClick={() => onRequest(next)}
  >
    <span style={{ display: 'inline-flex' }}>{next === 'paused' ? Icons.pause : Icons.play}</span>
    <span className="phone-long">{label}</span>
    <span className="phone-short">{keepPaused ? 'Keep paused' : next === 'paused' ? 'Pause' : 'Resume'}</span>
  </button>;
}
